import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button } from "@medusajs/ui";
import { Form } from "@repo/design-system";
import { memo, useId } from "react";
import { useForm } from "react-hook-form";
import {
  addressSchema,
  emptyAddressFormValues,
  type AddressFormData
} from "./address-schema";
import { AddressFormFields } from "./address-form-fields";

interface AddressFormProps {
  /** Initial values for the form (for edit mode) */
  defaultValues?: Partial<AddressFormData>;
  /** Callback when form is submitted successfully */
  onSubmit: (data: AddressFormData) => void | Promise<void>;
  /** Callback when form is cancelled. Omit to render the form without a Cancel button. */
  onCancel?: () => void;
  /** Whether to ask for the address nickname. Default: true */
  showAddressName?: boolean;
  /** Whether form submission is in progress */
  isSubmitting?: boolean;
  /** Server error message to display */
  serverError?: string | null;
  /** Label for the submit button */
  submitLabel?: string;
}

/**
 * Address form component with validation
 *
 * Features:
 * - Zod schema validation with react-hook-form
 * - Accessible form fields with proper labels
 * - Error messages for each field
 * - Loading state handling
 * - Responsive two-column layout
 */
export const AddressForm = memo(function AddressForm({
  defaultValues,
  onSubmit,
  onCancel,
  showAddressName = true,
  isSubmitting = false,
  serverError,
  submitLabel = "Save address"
}: AddressFormProps) {
  const formId = useId();

  const form = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      ...emptyAddressFormValues,
      ...defaultValues
    }
  });

  const handleSubmit = form.handleSubmit(async (data) => {
    await onSubmit(data);
  });

  return (
    <Form {...form}>
      <form
        id={formId}
        onSubmit={handleSubmit}
        className="space-y-4"
        aria-label="Address form"
      >
        <AddressFormFields form={form} showAddressName={showAddressName} />

        {serverError && (
          <Alert variant="error" dismissible>
            {serverError}
          </Alert>
        )}

        <div className="flex justify-end gap-3 pt-4">
          {onCancel && (
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button
            type="submit"
            isLoading={isSubmitting}
            disabled={isSubmitting}
          >
            {submitLabel}
          </Button>
        </div>
      </form>
    </Form>
  );
});
